// @ts-nocheck
import querystringify from "querystringify";
import Form, { Field } from "rc-field-form";
import * as React from "react";
import { UserService } from "../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { TitleManager } from "../../shared/utility/title-manager";
import AuthCheckYourInboxSvg from "../../svgs/auth-check-your-inbox.svg";
import AuthResetPasswordSvg from "../../svgs/auth-reset-password.svg";
import KuikaButton, {
  KUIKA_BUTTON_SUCCESS_HOLD_MS,
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import { toastService } from "../ui-kit/kuika-toast/toast-service";
import KuikaH1 from "../ui-kit/typography/H1/H1";
import KuikaH4 from "../ui-kit/typography/H4/H4";
import KuikaSmallMediumText from "../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaText from "../ui-kit/typography/Text/Text";
import { AuthLogo } from "./auth-logo";

export interface IForgotPasswordState {
  isCheckYourMailOpen: boolean;
  /**
   * The "check your inbox" view is on screen. It trails `isCheckYourMailOpen` by the ✓ hold, so the
   * button's own confirmation is seen before the view changes under it.
   */
  isInboxShown: boolean;
  isLoading: boolean;
  countdown: number;
  email: string;
}

/** What the send button says while the link is on its way and once it has gone. */
const SEND_LINK_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Sending", success: "Sent" };

export interface IForgotPasswordProps {}

interface IPageQueries {
  username?: string;
}

interface IForgotPasswordFormValues {
  Email: string;
}

class ForgotPassword extends React.PureComponent<IForgotPasswordProps & IWithHistoryProp, IForgotPasswordState> {
  timer: NodeJS.Timeout | null = null;

  private inboxRevealTimer: number | undefined = undefined;

  constructor(props: IForgotPasswordProps & IWithHistoryProp) {
    super(props);
    this.state = {
      isCheckYourMailOpen: false,
      isInboxShown: false,
      isLoading: false,
      countdown: 5,
      email: ""
    };
  }

  componentDidMount() {
    TitleManager.setPageTitle("Reset Your Password | Kuika", "Main Low Code Reset Password");
    TitleManager.setPageMeta(
      "Forgot your Kuika password? Reset it securely and regain access to your account in minutes."
    );
  }

  /**
   * The request went through: the ✓ goes up on the button first and the inbox view follows it.
   * Done here, off the flag the request handler already sets, so the handler — and the redirect
   * countdown it starts — are left exactly as they were.
   */
  componentDidUpdate(_prevProps: IForgotPasswordProps & IWithHistoryProp, prevState: IForgotPasswordState): void {
    if (this.state.isCheckYourMailOpen && !prevState.isCheckYourMailOpen) {
      this.inboxRevealTimer = window.setTimeout((): void => {
        this.inboxRevealTimer = undefined;
        this.setState({ isInboxShown: true });
      }, KUIKA_BUTTON_SUCCESS_HOLD_MS);
    }
  }

  componentWillUnmount() {
    TitleManager.clearPageTitle();
    if (this.timer) {
      clearInterval(this.timer);
    }
    if (this.inboxRevealTimer !== undefined) {
      window.clearTimeout(this.inboxRevealTimer);
    }
  }

  private getSubmitStatus = (): KuikaButtonStatus => {
    if (this.state.isLoading) {
      return "loading";
    }
    if (this.state.isCheckYourMailOpen) {
      return "success";
    }
    return "idle";
  };

  /** The button stays clickable while its ✓ is up, so a second send is kept out here. */
  private handleFinish = (data: IForgotPasswordFormValues): void => {
    if (this.state.isLoading || this.state.isCheckYourMailOpen) {
      return;
    }
    void this.onSubmit(data);
  };

  goToLogin = () => {
    this.props.navigator.gotoLogin();
  };

  onSubmit = async (data: any) => {
    this.setState({ isLoading: true });
    try {
      const userStatus = await UserService.GetUserStatus(data.Email);
      if (!userStatus || userStatus.status !== 200) {
        toastService.error("No account found with this email address");
        this.setState({ isLoading: false });
        return;
      }

      UserService.ForgotPassword(data).then(
        (res) => {
          if (res && res.status === 200) {
            this.setState({ isCheckYourMailOpen: true, isLoading: false, countdown: 5 }, () => {
              this.timer = setInterval(() => {
                this.setState(
                  (prevState) => ({ countdown: prevState.countdown - 1 }),
                  () => {
                    if (this.state.countdown === 0) {
                      if (this.timer) clearInterval(this.timer);
                      this.props.navigator.goToChangePassword(encodeURIComponent(data.Email));
                    }
                  }
                );
              }, 1000);
            });
          } else {
            this.setState({ isLoading: false });
          }
        },
        (error) => {
          if (error.response.data.message) {
            toastService.error(error.response.data.message);
            this.setState({ isLoading: false });
          }
        }
      );
    } catch (error) {
      this.setState({ isLoading: false });
      KMainFunctions.exceptionHandler(error);
    }
  };

  getUserEmailFromURL() {
    const querySearch = this.props?.navigator?.location?.search;
    if (querySearch) {
      const queryParams = querystringify.parse(querySearch) as IPageQueries;
      if (queryParams.username) {
        return queryParams.username;
      }
    }

    return "";
  }

  render() {
    const userEmail = this.getUserEmailFromURL();
    const isMainAI = KMainFunctions.isMainAI();

    return (
      <div className="email-confirmation">
        <div className="email-confirmation__content">
          <div className="email-confirmation__top-section">
            <AuthLogo />

            <div className="email-confirmation__check-inbox-card">
              {this.state.isInboxShown ? (
                <>
                  <div className="email-confirmation__title-row">
                    <AuthCheckYourInboxSvg />
                    <KuikaH4>Check your inbox</KuikaH4>
                  </div>

                  <div className="email-confirmation__line" />

                  <div className="email-confirmation__countdown-wrapper">
                    <KuikaH1 style={{ color: "var(--dark-8)", margin: 0 }}>{this.state.countdown}</KuikaH1>
                  </div>

                  <KuikaText className="email-confirmation__message-text">
                    Your password change request has been received. You can renew your password with the verification
                    code email sent to you.
                  </KuikaText>
                </>
              ) : (
                <>
                  <div className="email-confirmation__title-row">
                    <AuthResetPasswordSvg />
                    <KuikaH4>Reset password</KuikaH4>
                  </div>

                  <Form
                    onFinish={this.handleFinish}
                    initialValues={{ Email: userEmail }}
                    style={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}
                  >
                    <Field
                      name="Email"
                      rules={[
                        { required: true, message: "Please enter your email" },
                        { type: "email", message: "Please enter a valid email address" }
                      ]}
                    >
                      {(control, meta) => (
                        <div style={{ width: "100%" }}>
                          <KuikaInput
                            {...control}
                            leftIcon={<KuikaSvg name="mail" />}
                            placeholder="E-Mail"
                            size="lg"
                            error={meta.errors.length > 0}
                            readOnly={!!userEmail}
                            onChange={(e) => {
                              control.onChange(e);
                              this.setState({ email: e.target.value });
                            }}
                          />
                          <div style={{ minHeight: "20px" }}>
                            {meta.errors.length > 0 && (
                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <KuikaSvg name="info-circle" width={12} height={12} color="var(--red-5)" />
                                <KuikaSmallMediumText style={{ color: "var(--red-5)" }}>
                                  {meta.errors[0]}
                                </KuikaSmallMediumText>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Field>

                    <div className="email-confirmation__return-btn">
                      <KuikaButton
                        htmlType="submit"
                        size="lg"
                        status={this.getSubmitStatus()}
                        statusLabels={SEND_LINK_STATUS_LABELS}
                        disabled={!this.state.email && !userEmail}
                        style={{ width: "100%" }}
                        uppercase
                      >
                        Send link
                      </KuikaButton>
                    </div>

                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <KuikaButton
                        type="tertiary"
                        size="lg"
                        uppercase
                        onClick={this.goToLogin}
                        style={{ width: "100%" }}
                      >
                        Return to sign in
                      </KuikaButton>
                    </div>
                  </Form>
                </>
              )}
            </div>
          </div>

          {!isMainAI && (
            <div className="email-confirmation__bottom-section">
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/privacy", "_blank")}
              >
                Privacy
              </KuikaButton>
              <KuikaButton
                type="text"
                size="sm"
                uppercase
                onClick={() => window.open("https://www.kuika.com/en/legal/terms", "_blank")}
              >
                Terms
              </KuikaButton>
            </div>
          )}
        </div>
      </div>
    );
  }
}

const forgotPassword = withHistory<IForgotPasswordProps>(ForgotPassword);
export { forgotPassword as ForgotPassword };
